/**
 * The push hook: one WebSocket whose only job is to say "something changed, at
 * sequence N".
 *
 * THE FRAME CARRIES A NUMBER AND NOTHING ELSE, and that is the whole reason
 * this file can be small. `model/optimistic.ts` states the consequence: a
 * dropped frame, a Worker deploy that restarts every Durable Object and
 * disconnects every live socket, a train tunnel, and a two-week holiday all
 * reduce to the same already-tested `catchUpNeeded(localSeq, hint)` →
 * `/changes?since=` call. So this hook never reconciles anything, never holds
 * mail, and never needs to know what the client already has. It delivers hints
 * and reports whether it is connected.
 *
 * IT IS AN OPTIMISATION, NOT A DEPENDENCY. Everything it does is also achieved
 * by polling `/changes` on a timer, just later. That is why nothing in here is
 * allowed to throw: `new WebSocket` throws synchronously on a blocked or
 * mixed-content URL, `send` throws on a socket that closed between the check
 * and the call, and either one escaping into React would replace a working
 * mailbox with an error boundary because the *live update* channel failed.
 * `shell-ia.md` is explicit that a dropped socket is a grey dot and a 60 s
 * refresh, not a banner: "nothing is actually broken".
 *
 * THE WIRE CONTRACT, read off `apps/worker/src/index.ts` and `hub.ts` rather
 * than assumed:
 *
 *  - `/_hx/ws` on the same origin. Nothing is passed — no query string, no
 *    token. Cloudflare Access authenticates the upgrade at the hostname, and
 *    the Worker names the hub from the VERIFIED identity. A `?account=` was how
 *    it used to work and was a real leak (any family member could subscribe to
 *    the owner's hint stream); the Worker now 403s a mismatch, so sending one
 *    would at best be noise and at worst a rejected upgrade.
 *  - The hub broadcasts the literal string `{"type":"hint","seq":<number>}`.
 *  - Sending exactly `ping` gets `pong` back. Every other client message is
 *    dropped on the floor by `MailboxHub.webSocketMessage`.
 *
 * THE OTHER HALF OF THIS FILE IS WEB PUSH, and it exists because everything
 * above stops at the edge of the tab. A socket needs a running page: close the
 * laptop, swipe the tab away, leave the phone in a pocket, and the hint has
 * nowhere to land. `syncWebPush` registers a service worker and hands the
 * browser's own push subscription to the Worker, which fans the SAME frame out
 * to it when nobody is connected. Two rules govern it and both are written out
 * where they are enforced: the push carries no more than the socket frame does
 * (see `apps/worker/src/push.ts`), and no permission is ever requested without
 * a tap (`enableWebPush`, never `syncWebPush`).
 *
 * A DEFECT THAT USED TO LIVE HERE, kept because the shape recurs: the Worker
 * named the hub from the Access email while the origin posted hints naming
 * `accounts.address`, and `HEARTH_IDENTITIES` supports `login=account:role` for
 * exactly the case where those two differ. An aliased login connected, pinged
 * happily and received nothing for ever — silent at both ends, and not a leak,
 * because such a reader got less than they should rather than somebody else's.
 * The Worker now asks the origin's `/whoami` who the caller is before naming
 * the object; there is still nothing THIS side could have detected, since
 * silence is also what a quiet mailbox looks like.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { b64urlToBytes } from '@hearth/core/access'
import type { ChangeHint } from './model/optimistic.ts'

/**
 * Hardcoded rather than imported from `apps/worker/src/routes.ts`, matching
 * what `api.ts` does with `/_hx/v1`: the client is a separate build that must
 * not pull Worker code into its bundle. `routes.ts` is the source of truth and
 * this is a copy of it; the dev server proxies `/_hx` with `ws: true` so the
 * same path works without a Worker in front.
 */
export const PUSH_PATH = '/_hx/ws'

/** First reconnect wait, before jitter. Doubles per consecutive failure. */
export const PUSH_BASE_DELAY_MS = 1_000

/**
 * The ceiling on the reconnect wait, from `data.md`: "exponential backoff +
 * jitter capped at 30 s".
 *
 * The cap is what makes the failure mode acceptable rather than merely quiet.
 * Uncapped doubling reaches hours, and a laptop that was asleep through a
 * Worker deploy would come back and sit disconnected past any point where the
 * user would call it broken. At 30 s the worst case is one poll interval.
 */
export const PUSH_MAX_DELAY_MS = 30_000

/**
 * How often to send `ping`.
 *
 * THIS NUMBER IS CHOSEN, NOT MEASURED, and it is being written down as such
 * because the house rule is that numbers in comments are measured. Nothing here
 * has watched a Hearth socket get reaped, so there is no honest figure for how
 * long an idle WebSocket survives the path browser → Cloudflare edge → Durable
 * Object. 30 s is comfortably below every intermediary idle timeout the author
 * is aware of, and the cost of being wrong on the low side is four bytes a
 * minute per open tab.
 *
 * IT NO LONGER WAKES THE HUB, which used to be the second reason not to shorten
 * it. `MailboxHub` answered `ping` from `webSocketMessage`, so every keepalive
 * loaded the Durable Object back into memory and billed duration; it now
 * installs `setWebSocketAutoResponse(new WebSocketRequestResponsePair('ping',
 * 'pong'))` in its constructor and the runtime answers without waking anything.
 * THE WIRE CONTRACT IS UNCHANGED — send `ping`, receive the literal `pong` —
 * which is why nothing here had to move, and why the hub keeps its
 * `webSocketMessage` branch as the fallback.
 */
export const PUSH_PING_INTERVAL_MS = 30_000

/**
 * How long a `pong` may take before the socket is presumed dead.
 *
 * THE PING EXISTS FOR THE SILENT DEATH, not for the reap. A TCP connection that
 * has been black-holed — a laptop lid closed and reopened on a different
 * network, a NAT rebind, a phone leaving wifi mid-tunnel — leaves a WebSocket
 * whose `readyState` is still OPEN and whose `onclose` never fires. Without an
 * application-level round trip there is no event to react to and the tab sits
 * there believing it is live for ever. This is the only thing that catches it.
 */
export const PUSH_PONG_TIMEOUT_MS = 10_000

export type PushState =
  /** `enabled` is false. Nothing is connected and nothing is scheduled. */
  | 'disabled'
  /** No `WebSocket` in this runtime. Not an error — poll instead. */
  | 'unsupported'
  /** A first socket is in flight. Not yet degraded; do not shout. */
  | 'connecting'
  /** Live. Hints arrive as they happen. */
  | 'open'
  /** Was open, is not now. THIS is the state the grey dot renders. */
  | 'reconnecting'

export interface PushStatus {
  readonly state: PushState
  /**
   * Consecutive failed connections since the last successful open, so the UI
   * can distinguish "blinked" from "the edge has been gone for a minute"
   * without a second timer of its own. Zero while open.
   */
  readonly attempts: number
  /** The highest seq this socket has delivered. Status and debugging only. */
  readonly lastSeq: number | null
}

export interface UsePushOptions {
  /**
   * Called for every well-formed hint frame, in arrival order.
   *
   * MAY BE A FRESH ARROW ON EVERY RENDER; see the ref below. Duplicate and
   * out-of-order sequences are NOT filtered here — the hook does not know the
   * client's local sequence, and `catchUpNeeded` is where that comparison
   * belongs. Delivering a hint twice costs one `/changes` call that returns
   * nothing; dropping one costs mail that never appears.
   */
  readonly onHint: (hint: ChangeHint) => void
  /** Default true. False tears the socket down and schedules nothing. */
  readonly enabled?: boolean
}

/**
 * `ws://` or `wss://` for the same origin, matching the page.
 *
 * `wss` IS NOT OPTIONAL ON AN HTTPS PAGE: a `ws://` URL from an https document
 * is blocked as mixed content, and the constructor throws synchronously rather
 * than firing an error event — which is one of the two ways `new WebSocket`
 * can take the shell down if it is not wrapped.
 */
export function pushUrl(from: { protocol: string; host: string } = globalThis.location): string {
  return `${from.protocol === 'https:' ? 'wss:' : 'ws:'}//${from.host}${PUSH_PATH}`
}

/**
 * The wait before reconnect attempt number `attempt` (0-based).
 *
 * JITTER IS THE POINT, and the failure it prevents is specific to how this
 * system fails: a Worker deploy restarts every Durable Object and drops every
 * socket in the household in the same millisecond. With a fixed delay all of
 * them come back in the same millisecond too, at every rung of the backoff —
 * a thundering herd that is small in absolute terms but perfectly synchronised,
 * and perfectly synchronised is what turns "five reconnects" into "five
 * reconnects that all miss, then five more". The same applies to the tabs of
 * one user, which is the more likely case here.
 *
 * EQUAL jitter rather than full jitter: the delay is uniform over the top half
 * of the window, so it is spread but never near zero. Full jitter can return
 * ~0 ms repeatedly, which reconnects into an edge that is still mid-deploy and
 * spends attempts to learn nothing.
 */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const nominal = Math.min(PUSH_MAX_DELAY_MS, PUSH_BASE_DELAY_MS * 2 ** Math.max(0, attempt))
  return Math.round(nominal / 2 + random() * (nominal / 2))
}

function isHint(value: unknown): value is ChangeHint {
  if (typeof value !== 'object' || value === null) return false
  const frame = value as { readonly type?: unknown; readonly seq?: unknown }
  // `type` IS CHECKED, not assumed. The hub sends one frame shape today; a
  // future one that this build does not understand must be ignored rather than
  // read as a hint with `seq: undefined`, which would reach `catchUpNeeded` as
  // `undefined > n` — false, silently, for ever.
  return frame.type === 'hint' && typeof frame.seq === 'number' && Number.isFinite(frame.seq)
}

/**
 * Where the browser's own subscription is registered, and where the service
 * worker is fetched from.
 *
 * Both are copies of a fact that lives elsewhere — `PUSH_PATH` in
 * `apps/worker/src/routes.ts`, and `sw.js` in `vite.config.ts`'s
 * `entryFileNames` — for the same reason `PUSH_PATH` above is a copy: the
 * client is a separate build and must not pull Worker code into its bundle.
 *
 * THE SERVICE WORKER PATH IS ALSO A SCOPE. A worker controls the directory it
 * is served from, so `/sw.js` controls the whole site and `/assets/sw.js` would
 * control nothing that matters. The build pins it at the root; this string is
 * the other end of that pin.
 */
export const PUSH_SUBSCRIBE_PATH = '/_hx/push'
export const SERVICE_WORKER_PATH = '/sw.js'

export type WebPushState =
  /** This browser cannot do it at all, or not until it is installed. See `detail`. */
  | 'unsupported'
  /** The server has no VAPID key, so there is nothing to subscribe to. */
  | 'unconfigured'
  /** The reader said no, or the browser said it for them. Only they can undo it. */
  | 'blocked'
  /** Supported, configured, and not asked for. The resting state. */
  | 'off'
  /** Subscribed, and the server has been told which device. */
  | 'on'
  /** Something threw. `detail` says what; the socket is unaffected. */
  | 'failed'

export interface WebPushStatus {
  readonly state: WebPushState
  /** One sentence a settings screen can render verbatim. Null when there is nothing to add. */
  readonly detail: string | null
}

/**
 * WHY THIS IS NOT `'Notification' in window`.
 *
 * On iOS, `Notification` is undefined in a Safari TAB and defined in the same
 * site once it has been added to the Home Screen — the API is gated on being
 * installed, not on the browser. So the bare check is true and useless: it says
 * "unsupported" to a person holding a device that supports it perfectly well
 * and is four taps away. `navigator.standalone` is the (non-standard, iOS-only)
 * flag that tells the two apart, so an iPhone gets the instruction and a
 * genuinely incapable browser gets the refusal.
 */
export function webPushUnavailable(): string | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return 'This browser cannot show notifications for a site that is not open.'
  }
  if (typeof window !== 'undefined' && !('PushManager' in window)) {
    return 'This browser cannot show notifications for a site that is not open.'
  }
  if (typeof Notification === 'undefined') {
    // `standalone` exists only on iOS Safari, and is false in a tab.
    const ios = (navigator as Navigator & { standalone?: boolean }).standalone === false
    return ios
      ? 'On iPhone and iPad, notifications need Hearth on the Home Screen: tap Share, then Add to Home Screen, and open it from there.'
      : 'This browser cannot show notifications for a site that is not open.'
  }
  return null
}

/** The public key this server signs with, or null when push is not set up. */
async function applicationServerKey(): Promise<string | null> {
  const response = await fetch(PUSH_SUBSCRIBE_PATH, { headers: { accept: 'application/json' } })
  if (!response.ok) return null
  const body = (await response.json()) as { configured?: boolean; key?: string | null }
  return body.configured && typeof body.key === 'string' && body.key.length > 0 ? body.key : null
}

function bytesToB64url(buffer: ArrayBuffer): string {
  let binary = ''
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Bring this device's subscription in line with the server, WITHOUT ASKING FOR
 * ANYTHING.
 *
 * IT NEVER CALLS `requestPermission`, and that is the rule the whole shape of
 * this file follows from. `interaction.md:499` forbids the native prompt on
 * load, and it is right for a reason beyond taste: a prompt the reader did not
 * ask for is usually answered with "Block", which is a permanent decision they
 * have to go into browser settings to reverse. So this runs on every load and
 * does nothing visible unless permission has ALREADY been granted — on this
 * device, in an earlier session, or in the browser's own site settings.
 *
 * IT RE-POSTS THE SUBSCRIPTION EVERY TIME rather than remembering that it once
 * did. The cheap-looking alternative — a flag in localStorage — is wrong in the
 * direction that matters: the server's copy can disappear (a Durable Object
 * that was never migrated, a subscription evicted at the cap, a rotated VAPID
 * key) and a client that believes it has already registered would never repair
 * it, silently, for as long as the flag survives. The cost is one small POST to
 * a Durable Object that this same page wakes anyway a moment later by opening
 * its WebSocket.
 */
export async function syncWebPush(): Promise<WebPushStatus> {
  const unavailable = webPushUnavailable()
  if (unavailable) return { state: 'unsupported', detail: unavailable }

  try {
    // REGISTERED EVEN WHEN PERMISSION HAS NOT BEEN GRANTED. Registration asks
    // the reader for nothing, and doing it here means `enableWebPush` — which
    // runs inside a tap, where browsers give a handler a short leash — has a
    // worker waiting rather than a network round trip to make first.
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
    if (Notification.permission !== 'granted') {
      const existing = await registration.pushManager?.getSubscription()
      // A permission that was REVOKED leaves the subscription object behind and
      // dead. Dropping it here keeps "the switch is on" from meaning two
      // different things on this device and on the server.
      if (existing) await existing.unsubscribe().catch(() => undefined)
      return {
        state: Notification.permission === 'denied' ? 'blocked' : 'off',
        detail:
          Notification.permission === 'denied'
            ? 'This browser is set to block notifications from Hearth. Only its own site settings can undo that.'
            : null,
      }
    }
    return await subscribeAndTell(registration)
  } catch (error) {
    // NOTHING HERE MAY THROW INTO REACT. Everything this file does is an
    // optimisation over a 60 s poll, and a mailbox that renders an error
    // boundary because a service worker failed to register would be a strictly
    // worse client than one with no notifications at all.
    return { state: 'failed', detail: error instanceof Error ? error.message : 'Notifications could not be set up.' }
  }
}

/**
 * Subscribe if needed, then tell the server which device this is.
 *
 * THE KEY IS COMPARED, NOT ASSUMED. A `PushSubscription` is bound to the
 * application server key it was created with: sign with a different one and the
 * push service answers 403 for ever, which from the reader's side is a switch
 * that says "on" and a phone that never buzzes. So an existing subscription
 * whose key is not the one this server just named is torn down and made again.
 * That is the whole recovery path for a VAPID rotation, and it costs one
 * comparison per load.
 */
async function subscribeAndTell(registration: ServiceWorkerRegistration): Promise<WebPushStatus> {
  const key = await applicationServerKey()
  if (!key) {
    return {
      state: 'unconfigured',
      detail: 'This server has no notification keys yet, so there is nothing to subscribe to.',
    }
  }

  const manager = registration.pushManager
  let subscription = await manager.getSubscription()
  if (subscription) {
    const current = subscription.options?.applicationServerKey
    if (current && bytesToB64url(current as ArrayBuffer) !== key) {
      await subscription.unsubscribe().catch(() => undefined)
      subscription = null
    }
  }
  if (!subscription) {
    subscription = await manager.subscribe({
      // REQUIRED TO BE TRUE BY EVERY BROWSER THAT IMPLEMENTS THIS, and honestly
      // so: a push that shows nothing is a page being woken in the background
      // without the reader knowing. `sw.ts` always shows one.
      userVisibleOnly: true,
      // Passed as bytes rather than as the base64url string. The string form is
      // in the spec and Safari has historically not accepted it.
      applicationServerKey: b64urlToBytes(key) as BufferSource,
    })
  }

  const response = await fetch(PUSH_SUBSCRIBE_PATH, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(subscription.toJSON()),
  })
  if (!response.ok) {
    return { state: 'failed', detail: `The server would not store this device (${response.status}).` }
  }
  return { state: 'on', detail: null }
}

/**
 * Ask for permission and subscribe. CALL THIS FROM A CLICK AND NOWHERE ELSE.
 *
 * Browsers require a user gesture for `requestPermission` — Safari refuses
 * outright without one, Chrome refuses in an iframe and increasingly elsewhere
 * — so this is the one function that prompts, and it exists to be wired to a
 * switch in Settings. Everything else in this file assumes the answer.
 */
export async function enableWebPush(): Promise<WebPushStatus> {
  const unavailable = webPushUnavailable()
  if (unavailable) return { state: 'unsupported', detail: unavailable }
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return {
        state: permission === 'denied' ? 'blocked' : 'off',
        detail:
          permission === 'denied'
            ? 'This browser is set to block notifications from Hearth. Only its own site settings can undo that.'
            : null,
      }
    }
    return await syncWebPush()
  } catch (error) {
    return { state: 'failed', detail: error instanceof Error ? error.message : 'Notifications could not be set up.' }
  }
}

/**
 * Stop notifying this device.
 *
 * BOTH ENDS, AND THE SERVER FIRST-CLASS. Unsubscribing in the browser alone
 * would leave a record on the server that is pushed to for ever and answered by
 * nobody — the push service eventually 404s it and the Worker deletes it, but
 * "eventually" is not what somebody who just turned a switch off means. The
 * permission itself is deliberately NOT revoked: no API can, and a browser that
 * remembers the grant is what makes turning it back on a single tap.
 */
export async function disableWebPush(): Promise<WebPushStatus> {
  const unavailable = webPushUnavailable()
  if (unavailable) return { state: 'unsupported', detail: unavailable }
  try {
    // NO ARGUMENT, and `getRegistration(SERVICE_WORKER_PATH)` is the plausible
    // wrong one: the parameter is a DOCUMENT url to find the registration that
    // controls it, not the worker's own script. Bare, it asks about this page,
    // which is exactly the question.
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = await registration?.pushManager?.getSubscription()
    if (subscription) {
      await fetch(PUSH_SUBSCRIBE_PATH, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      }).catch(() => undefined)
      await subscription.unsubscribe().catch(() => undefined)
    }
    return { state: 'off', detail: null }
  } catch (error) {
    return { state: 'failed', detail: error instanceof Error ? error.message : 'That device could not be unsubscribed.' }
  }
}

const IDLE_WEB_PUSH: WebPushStatus = { state: 'off', detail: null }

/**
 * The web-push half as a hook, for the screen that owns the switch.
 *
 * SEPARATE FROM `usePush` BECAUSE THE CONSUMERS ARE. The shell wants a socket
 * and gets the subscription kept alive as a side effect; a settings screen wants
 * the state, the enable and the disable, and does not want a WebSocket for
 * them. Both call the same three functions, so there is one implementation and
 * two doors onto it.
 */
export function useWebPush(): {
  readonly status: WebPushStatus
  readonly enable: () => Promise<void>
  readonly disable: () => Promise<void>
} {
  const [status, setStatus] = useState<WebPushStatus>(IDLE_WEB_PUSH)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    void syncWebPush().then((next) => {
      if (live.current) setStatus(next)
    })
    return () => {
      live.current = false
    }
  }, [])

  const enable = useCallback(async (): Promise<void> => {
    const next = await enableWebPush()
    if (live.current) setStatus(next)
  }, [])

  const disable = useCallback(async (): Promise<void> => {
    const next = await disableWebPush()
    if (live.current) setStatus(next)
  }, [])

  return { status, enable, disable }
}

export function usePush({ onHint, enabled = true }: UsePushOptions): PushStatus & { readonly webPush: WebPushStatus } {
  const [status, setStatus] = useState<PushStatus>(() => ({
    state: enabled ? 'connecting' : 'disabled',
    attempts: 0,
    lastSeq: null,
  }))

  /**
   * THE CALLBACK LIVES IN A REF SO THE EFFECT DOES NOT DEPEND ON IT.
   *
   * Every realistic caller writes `usePush({ onHint: (h) => { ... } })` inline,
   * which is a new function identity on every render. With `onHint` in the
   * dependency array that tears down and rebuilds the socket on every render of
   * the shell — so typing in the search box would open a WebSocket per
   * keystroke, and each one would arrive, be replaced before `onopen`, and be
   * closed. The connection would never live long enough to deliver anything,
   * and the symptom is "push just doesn't work" with no error anywhere.
   */
  const handler = useRef(onHint)
  useEffect(() => {
    handler.current = onHint
  }, [onHint])

  /**
   * KEEPING AN EXISTING GRANT ALIVE IS THIS HOOK'S JOB, and it is separate from
   * the socket's effect on purpose: a re-registered service worker must not
   * mean a torn-down WebSocket, and a socket that reconnects forty times in a
   * bad hour must not mean forty POSTs.
   *
   * IT ASKS FOR NOTHING. `syncWebPush` never prompts — it registers the worker
   * and, only if permission is already granted, makes sure the server knows
   * about this device. So this is reachable today for anyone who has granted
   * notifications in an earlier session or in their browser's site settings,
   * and it is silent for everybody else until Settings grows the switch that
   * calls `enableWebPush` from a tap.
   */
  const [webPush, setWebPush] = useState<WebPushStatus>(IDLE_WEB_PUSH)
  useEffect(() => {
    if (!enabled) return
    let live = true
    void syncWebPush().then((next) => {
      if (live) setWebPush(next)
    })
    return () => {
      live = false
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setStatus({ state: 'disabled', attempts: 0, lastSeq: null })
      return
    }

    // Read off `globalThis` at connect time rather than captured at module
    // load, because that is what a test replaces and what a polyfill would
    // install late. Absent, this is a runtime without WebSocket at all — an
    // old embedded webview, or any non-DOM environment the module is imported
    // into — and the answer is to say so and let the caller poll.
    const Sock = globalThis.WebSocket
    if (typeof Sock !== 'function') {
      setStatus({ state: 'unsupported', attempts: 0, lastSeq: null })
      return
    }

    let stopped = false
    let socket: WebSocket | null = null
    let reconnect: ReturnType<typeof setTimeout> | undefined
    let keepalive: ReturnType<typeof setInterval> | undefined
    let deadline: ReturnType<typeof setTimeout> | undefined
    let attempts = 0

    /**
     * Publish a status change, and only a change.
     *
     * React bails out of a re-render when a state updater returns the identical
     * object, and this hook is called from the shell — so a hint arriving every
     * few seconds, or a `pong` on every ping, must not re-render the message
     * list to report a state it was already in.
     */
    const publish = (next: Partial<PushStatus>): void => {
      setStatus((current) => {
        const merged = { ...current, ...next }
        return merged.state === current.state &&
          merged.attempts === current.attempts &&
          merged.lastSeq === current.lastSeq
          ? current
          : merged
      })
    }

    const clearKeepalive = (): void => {
      // `unref` does not exist in a browser, so a timer that is not explicitly
      // cleared survives the component that made it. Both of these are cleared
      // on every path out, including the ones that look like they cannot be
      // reached twice.
      if (keepalive !== undefined) clearInterval(keepalive)
      if (deadline !== undefined) clearTimeout(deadline)
      keepalive = undefined
      deadline = undefined
    }

    /**
     * Abandon the current socket.
     *
     * THE HANDLERS COME OFF BEFORE `close()`, and the order is load-bearing.
     * `close()` delivers a close event asynchronously, so a socket torn down on
     * unmount would run `onclose` → `retry()` → `setTimeout` AFTER the effect
     * cleanup had finished: a timer nothing owns any more, then a WebSocket
     * opened by an unmounted component, then a `setStatus` on it. The `stopped`
     * flag catches that too; detaching means the event never has to be reasoned
     * about at all, and also covers the pong-deadline path, where a socket is
     * abandoned while it is still, as far as the browser is concerned, open.
     */
    const drop = (): void => {
      clearKeepalive()
      const dying = socket
      socket = null
      if (!dying) return
      dying.onopen = null
      dying.onmessage = null
      dying.onclose = null
      dying.onerror = null
      try {
        dying.close()
      } catch {
        // `close()` on a socket the browser has already given up on can throw
        // (an InvalidAccessError for a bad code, a DOM exception on a detached
        // one). There is nothing to do about a socket being extra closed.
      }
    }

    function retry(): void {
      // GUARDED BECAUSE TWO PATHS REACH HERE FOR ONE DEAD SOCKET: the pong
      // deadline expiring and the socket's own close event, plus `onerror`
      // which every browser follows with a close. Without the guard each one
      // schedules its own timer, and the reconnects double per failure in the
      // opposite direction from the backoff.
      if (stopped || reconnect !== undefined) return
      drop()
      attempts += 1
      publish({ state: 'reconnecting', attempts })
      reconnect = setTimeout(connect, backoffDelay(attempts - 1))
    }

    function connect(): void {
      reconnect = undefined
      if (stopped) return

      let next: WebSocket
      try {
        next = new Sock(pushUrl())
      } catch {
        // SYNCHRONOUS THROW, NOT AN ERROR EVENT: a mixed-content URL, a CSP
        // `connect-src` that excludes the origin, or a Safari private window
        // with the socket API disabled all throw right here. Uncaught this
        // propagates out of the effect and into React's error handling, and a
        // client that can read mail perfectly well over HTTP renders a blank
        // error boundary because the live-update channel was unavailable.
        retry()
        return
      }

      socket = next
      publish({ state: attempts === 0 ? 'connecting' : 'reconnecting' })

      next.onopen = (): void => {
        if (stopped || socket !== next) return
        attempts = 0
        publish({ state: 'open', attempts: 0 })

        keepalive = setInterval(() => {
          // A ping is already outstanding: the deadline owns this socket now,
          // and stacking a second ping would reset nothing and cost a wake.
          if (deadline !== undefined) return
          try {
            next.send('ping')
          } catch {
            // `send` throws on a socket that closed between the last event and
            // this tick — the ordinary race, not an exceptional one.
            retry()
            return
          }
          deadline = setTimeout(() => {
            deadline = undefined
            retry()
          }, PUSH_PONG_TIMEOUT_MS)
        }, PUSH_PING_INTERVAL_MS)
      }

      next.onmessage = (event: MessageEvent): void => {
        if (socket !== next) return
        const data: unknown = event.data
        // A Blob or ArrayBuffer is not part of the contract. Passing one to
        // `JSON.parse` would stringify it to "[object Blob]" and throw, which
        // is caught below, but ignoring it here says why.
        if (typeof data !== 'string') return

        if (data === 'pong') {
          if (deadline !== undefined) clearTimeout(deadline)
          deadline = undefined
          return
        }

        let frame: unknown
        try {
          frame = JSON.parse(data)
        } catch {
          // A frame this build does not understand is not a reason to drop a
          // working connection. Anything the hub adds later arrives here first.
          return
        }
        if (!isHint(frame)) return

        // Bookkeeping BEFORE the callback: a consumer that throws must not cost
        // the status update that has already been earned.
        publish({ lastSeq: frame.seq })
        try {
          handler.current(frame)
        } catch {
          // A throw from the consumer belongs to the consumer. Letting it out
          // of a WebSocket event handler puts it on `window.onerror`, where it
          // is reported as an unhandled error with no stack into the shell —
          // and it would abort this handler, so the NEXT hint would be the
          // first thing the caller ever saw work again.
        }
      }

      next.onclose = (): void => {
        if (socket !== next) return
        retry()
      }

      next.onerror = (): void => {
        // Deliberately empty. The spec requires a close event after an error
        // event, so `onclose` is the single place a failure is handled; doing
        // it in both is how one dead socket becomes two reconnect timers.
      }
    }

    connect()

    return () => {
      stopped = true
      if (reconnect !== undefined) clearTimeout(reconnect)
      reconnect = undefined
      drop()
    }
  }, [enabled])

  // One object, so a caller can read the socket's state and the device's
  // subscription without holding two hooks that both know about `/_hx/ws`.
  return useMemo(() => ({ ...status, webPush }), [status, webPush])
}
